import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design04/Header';
import {
    Coffee,
    Globe,
    Award,
    Shield,
    Star,
    MapPin,
    Mail,
    Phone,
    Users,
    Truck,
    CheckCircle,
    ArrowRight,
    Leaf,
    Heart,
    Zap,
    Target,
    TrendingUp,
    Package,
    Clock,
    ThumbsUp,
} from 'lucide-react';

const HomePage = () => {
    return (
        <>
            <title>Claude&apos;s Special - Premium Ceylon Cinnamon Exports | Sri Lankan Spices</title>
            <meta
                name="description"
                content="Claude's Special exports the finest Ceylon Cinnamon from Sri Lanka to Australia. Discover authentic, premium quality cinnamon with rich heritage and unmatched flavor."
            />
            <meta
                name="keywords"
                content="Ceylon Cinnamon, Sri Lankan spices, cinnamon export, authentic cinnamon, premium spices, Australia import"
            />
            <meta property="og:title" content="Claude's Special - Premium Ceylon Cinnamon Exports" />
            <meta
                property="og:description"
                content="Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage."
            />
            <meta property="og:type" content="website" />
            <link rel="canonical" href="/" />

            <Header />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50">
                {/* Background */}
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-orange-50/60 to-red-50/80"></div>
                    <div className="absolute inset-0 opacity-20">
                        <img
                            src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg"
                            alt="Ceylon Cinnamon plantation"
                            className="w-full h-full object-cover object-center"
                        />
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-orange-400/30 to-red-400/30 rounded-full blur-2xl animate-pulse"></div>
                    <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-yellow-400/30 to-orange-400/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
                    <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-gradient-to-br from-red-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse delay-500"></div>
                </div>

                <div className="relative z-10 text-center max-w-7xl mx-auto px-4 pt-28 pb-20">
                    {/* Badge */}
                    <div className="inline-flex items-center space-x-3 bg-white/90 backdrop-blur-xl rounded-full px-8 py-4 mb-12 border border-orange-200 shadow-xl hover:shadow-2xl transition-all duration-500">
                        <Coffee className="h-6 w-6 text-orange-600" />
                        <span className="text-lg font-bold text-gray-800">Premium Ceylon Heritage Since 1999</span>
                        <Star className="h-5 w-5 text-yellow-500" />
                    </div>

                    <h1 className="text-7xl md:text-8xl lg:text-9xl font-black mb-10 leading-none">
                        <span className="bg-gradient-to-r from-orange-600 via-red-500 to-orange-600 bg-clip-text text-transparent">
                            Claude&apos;s
                        </span>
                        <br />
                        <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent">
                            Special
                        </span>
                    </h1>

                    <p className="text-3xl md:text-4xl mb-10 font-medium text-orange-700">
                        Premium Ceylon Cinnamon Excellence
                    </p>

                    <p className="text-xl mb-20 text-gray-700 max-w-5xl mx-auto leading-relaxed">
                        From Sri Lanka&apos;s pristine highlands to Australia&apos;s finest establishments, we deliver the world&apos;s
                        most exquisite Ceylon Cinnamon with uncompromising quality and authenticity.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-8 justify-center items-center mb-24">
                        <Link
                            to="/ceylon-cinnamon"
                            className="group bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-12 py-6 rounded-2xl font-bold text-xl transition-all duration-500 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center space-x-3"
                        >
                            <span>Discover Excellence</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </Link>
                        <a
                            href="#about"
                            className="bg-white hover:bg-gray-50 text-gray-800 border-2 border-orange-300 hover:border-orange-400 px-12 py-6 rounded-2xl font-bold text-xl transition-all duration-500 shadow-lg hover:shadow-xl"
                        >
                            Our Story
                        </a>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 pb-16 border-t border-orange-200">
                        <div className="text-center group hover:scale-110 transition-transform duration-500">
                            <div className="text-4xl md:text-5xl font-black text-orange-600 mb-3">25+</div>
                            <div className="text-lg text-gray-700 font-semibold">Years Experience</div>
                        </div>
                        <div className="text-center group hover:scale-110 transition-transform duration-500">
                            <div className="text-4xl md:text-5xl font-black text-orange-600 mb-3">100%</div>
                            <div className="text-lg text-gray-700 font-semibold">Authentic Ceylon</div>
                        </div>
                        <div className="text-center group hover:scale-110 transition-transform duration-500">
                            <div className="text-4xl md:text-5xl font-black text-orange-600 mb-3">50+</div>
                            <div className="text-lg text-gray-700 font-semibold">Export Partners</div>
                        </div>
                        <div className="text-center group hover:scale-110 transition-transform duration-500">
                            <div className="text-4xl md:text-5xl font-black text-orange-600 mb-3">99.9%</div>
                            <div className="text-lg text-gray-700 font-semibold">Quality Rating</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-32 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="grid lg:grid-cols-2 gap-20 items-center">
                        <div className="relative">
                            <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-orange-200 to-red-200 rounded-2xl transform rotate-12"></div>
                            <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-orange-100">
                                <img
                                    src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                                    alt="Traditional cinnamon processing"
                                    className="w-full h-80 object-cover rounded-2xl mb-6"
                                />
                                <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 shadow-xl text-white">
                                    <div className="text-center">
                                        <Coffee className="h-8 w-8 mx-auto mb-2" />
                                        <div className="text-xl font-bold">Premium</div>
                                        <div className="text-sm opacity-90">Quality</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full px-6 py-3 mb-8 border border-orange-200">
                                <Award className="h-5 w-5" />
                                <span className="text-sm font-bold">Our Heritage</span>
                            </div>

                            <h2 className="text-5xl font-black text-gray-900 mb-8 leading-tight">
                                Crafting Excellence
                                <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent"> Since 1999</span>
                            </h2>

                            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
                                Born from a passion for authentic Sri Lankan heritage, Claude&apos;s Special has become the premier bridge
                                between Ceylon&apos;s ancient cinnamon traditions and Australia&apos;s most discerning markets.
                            </p>

                            <div className="space-y-6 mb-12">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Certified Excellence</h3>
                                        <p className="text-gray-600">
                                            ISO certification with HACCP compliance for the highest international standards.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
                                        <Users className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Artisan Partnership</h3>
                                        <p className="text-gray-600">Direct relationships with master craftsmen ensuring fair trade and sustainability.</p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                                        <Globe className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">Global Excellence</h3>
                                        <p className="text-gray-600">Seamless export operations connecting Sri Lankan excellence to Australian markets.</p>
                                    </div>
                                </div>
                            </div>

                            <Link
                                to="/ceylon-cinnamon"
                                className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl space-x-3"
                            >
                                <span>Learn More</span>
                                <ArrowRight className="h-5 w-5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-32 bg-gradient-to-br from-gray-50 to-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full px-8 py-4 mb-8 border border-orange-200">
                            <Star className="h-6 w-6" />
                            <span className="font-bold text-lg">Why Choose Us</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-8">
                            Unmatched Quality &amp;
                            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent"> Excellence</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                            Every aspect of our operation is designed to deliver the finest Ceylon Cinnamon experience
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Feature 1 */}
                        <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 hover:border-orange-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100 to-red-100 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Shield className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Premium Quality Assurance</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Every batch undergoes rigorous testing and quality control to ensure only the finest Ceylon Cinnamon
                                    reaches your customers.
                                </p>
                                <div className="flex items-center text-orange-600 font-semibold">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>ISO Certified</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 hover:border-orange-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Zap className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Fast Export Service</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Efficient logistics and priority customs clearance ensure your orders reach Australia quickly and
                                    safely.
                                </p>
                                <div className="flex items-center text-blue-600 font-semibold">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>5-10 Day Delivery</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 hover:border-orange-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Leaf className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Sustainable Sourcing</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Direct partnerships with local farmers ensure ethical practices and environmental sustainability.
                                </p>
                                <div className="flex items-center text-green-600 font-semibold">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Ethically Sourced</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 4 */}
                        <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 hover:border-orange-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Package className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Flexible Packaging</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Custom packaging solutions from bulk wholesale to retail-ready formats to meet your specific needs.
                                </p>
                                <div className="flex items-center text-purple-600 font-semibold">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Custom Solutions</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 5 */}
                        <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 hover:border-orange-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <ThumbsUp className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Expert Support</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Dedicated export specialists provide personalized support throughout your import journey.
                                </p>
                                <div className="flex items-center text-yellow-600 font-semibold">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>24/7 Support</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 6 */}
                        <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-orange-100 hover:border-orange-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <TrendingUp className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Competitive Pricing</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Direct sourcing and efficient operations allow us to offer premium Ceylon Cinnamon at competitive
                                    wholesale prices.
                                </p>
                                <div className="flex items-center text-teal-600 font-semibold">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Best Value Promise</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
                </div>

                <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
                    <div className="inline-flex items-center space-x-3 bg-white/20 backdrop-blur-xl rounded-full px-8 py-4 mb-10 border border-white/30">
                        <Coffee className="h-6 w-6 text-white" />
                        <span className="text-white font-bold text-lg">Ready to Start?</span>
                    </div>

                    <h2 className="text-5xl md:text-6xl font-black text-white mb-8 leading-tight">
                        Begin Your Ceylon <span className="text-yellow-300"> Cinnamon Journey</span>
                    </h2>

                    <p className="text-xl text-white/90 mb-16 leading-relaxed max-w-4xl mx-auto">
                        Join Australia&apos;s most successful importers who trust Claude&apos;s Special for authentic Ceylon
                        Cinnamon. Experience competitive wholesale pricing and expert export support.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <a
                            href="#contact"
                            className="group bg-white hover:bg-gray-50 text-orange-600 px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center space-x-3"
                        >
                            <span>Get Quote</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform duration-300" />
                        </a>
                        <Link
                            to="/ceylon-cinnamon"
                            className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/50 hover:border-white/80 px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 backdrop-blur-xl"
                        >
                            Explore Products
                        </Link>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-orange-100 to-red-100 text-orange-700 rounded-full px-8 py-4 mb-8 border border-orange-200">
                            <Mail className="h-6 w-6" />
                            <span className="font-bold text-lg">Get In Touch</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-8">
                            Ready to Import
                            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent"> Ceylon Cinnamon?</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-4xl mx-auto">
                            Contact our export specialists for wholesale pricing and custom solutions
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-16">
                        {/* Contact Info */}
                        <div className="space-y-8">
                            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-200">
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">Export Office</h3>
                                <div className="space-y-6">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <MapPin className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-1">Location</h4>
                                            <p className="text-gray-600">Colombo, Sri Lanka</p>
                                            <p className="text-orange-600 text-sm">Export Processing Zone</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Phone className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-1">Phone</h4>
                                            <p className="text-gray-600">+94 11 XXX XXXX</p>
                                            <p className="text-blue-600 text-sm">Export Hotline</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                            <Mail className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-1">Email</h4>
                                            <p className="text-gray-600">exports@claudesspecial.lk</p>
                                            <p className="text-green-600 text-sm">24/7 Response</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 border border-yellow-200">
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Export Specialties</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className="h-4 w-4 text-orange-500" />
                                        <span className="text-gray-700 text-sm">Cinnamon Sticks</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className="h-4 w-4 text-orange-500" />
                                        <span className="text-gray-700 text-sm">Ground Powder</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className="h-4 w-4 text-orange-500" />
                                        <span className="text-gray-700 text-sm">Essential Oil</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className="h-4 w-4 text-orange-500" />
                                        <span className="text-gray-700 text-sm">Custom Packaging</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-white rounded-2xl p-8 shadow-xl border border-orange-100">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Export Inquiry</h3>
                            <form className="space-y-6">
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Company Name *</label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors duration-200"
                                            placeholder="Your company"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Contact Person *</label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors duration-200"
                                            placeholder="Your name"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Email Address *</label>
                                    <input
                                        type="email"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors duration-200"
                                        placeholder="your@company.com"
                                    />
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Product Interest</label>
                                        <select className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors duration-200">
                                            <option value="">Select product</option>
                                            <option value="sticks">Cinnamon Sticks</option>
                                            <option value="powder">Ground Powder</option>
                                            <option value="oil">Essential Oil</option>
                                            <option value="mixed">Mixed Products</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Quantity Required</label>
                                        <select className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors duration-200">
                                            <option value="">Select quantity</option>
                                            <option value="1-100kg">1-100 kg</option>
                                            <option value="100-500kg">100-500 kg</option>
                                            <option value="500-1000kg">500-1000 kg</option>
                                            <option value="1000kg+">1000+ kg</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Message</label>
                                    <textarea
                                        rows={4}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors duration-200 resize-none"
                                        placeholder="Tell us about your import requirements, preferred packaging, delivery timeline, etc."
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-3"
                                >
                                    <span>Send Inquiry</span>
                                    <ArrowRight className="h-5 w-5" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-12 mb-12">
                        {/* Brand */}
                        <div className="md:col-span-2">
                            <div className="flex items-center space-x-3 mb-6">
                                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                                    <Coffee className="h-7 w-7 text-white" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-white">Claude&apos;s Special</div>
                                    <div className="text-orange-400 font-medium">Ceylon Cinnamon Exports</div>
                                </div>
                            </div>
                            <p className="text-gray-300 leading-relaxed mb-6 max-w-md">
                                Bringing the authentic taste and health benefits of Ceylon Cinnamon from Sri Lanka&apos;s highlands to
                                Australia&apos;s most discerning markets with uncompromising quality.
                            </p>
                            <div className="flex space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200 cursor-pointer">
                                    <span className="text-sm font-bold">🇱🇰</span>
                                </div>
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200 cursor-pointer">
                                    <span className="text-sm font-bold">🇦🇺</span>
                                </div>
                            </div>
                        </div>

                        {/* Products */}
                        <div>
                            <h4 className="text-lg font-bold mb-6 text-orange-400">Products</h4>
                            <ul className="space-y-3 text-gray-300">
                                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Ceylon Cinnamon Sticks</li>
                                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Ground Cinnamon Powder</li>
                                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Cinnamon Essential Oil</li>
                                <li className="hover:text-white transition-colors duration-200 cursor-pointer">Custom Packaging</li>
                            </ul>
                        </div>

                        {/* Quick Links */}
                        <div>
                            <h4 className="text-lg font-bold mb-6 text-orange-400">Quick Links</h4>
                            <ul className="space-y-3">
                                <li>
                                    <Link to="/ceylon-cinnamon" className="text-gray-300 hover:text-white transition-colors duration-200">
                                        About Ceylon Cinnamon
                                    </Link>
                                </li>
                                <li>
                                    <a href="#contact" className="text-gray-300 hover:text-white transition-colors duration-200">
                                        Export Inquiry
                                    </a>
                                </li>
                                <li>
                                    <a href="#about" className="text-gray-300 hover:text-white transition-colors duration-200">
                                        Our Story
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
                        <p className="text-gray-400 mb-4 md:mb-0">
                            &copy; 2025 Claude&apos;s Special. All rights reserved. | Export License: SL-EXP-2025-001
                        </p>
                        <div className="flex items-center space-x-6 text-gray-400 text-sm">
                            <span className="flex items-center space-x-2">
                                <Coffee className="h-4 w-4 text-orange-400" />
                                <span>100% Ceylon</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Shield className="h-4 w-4 text-green-400" />
                                <span>Certified Quality</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Globe className="h-4 w-4 text-blue-400" />
                                <span>Global Export</span>
                            </span>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default HomePage;
